import React, { useRef } from 'react';
import { Button } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import useGeneratePDF from '../../hooks/useGeneratePDF';
import '../../styles/reports.scss';

interface FuelLog {
  date?: string;
  fuelFilled?: number;
  fuelUsed?: number;
  amount?: number;
  odometer?: number;
  remainingFuel?: number;
  isFuelInvalid?: boolean;
  [key: string]: any;
}

interface FuelSummary {
  totalFuelFilled?: number;
  totalFuelConsumed?: number;
  totalFuelCost?: number;
  averageFuelUsage?: number;
  totalDistanceTraveled?: number;
  costPerKm?: number;
}

interface VehicleDetails {
  plateNumber?: string;
  fuelType?: string;
  fuelEfficiency?: number;
}

interface FuelSummaryReportProps {
  filteredFuelData: FuelLog[];
  fuelSummary: FuelSummary | null;
  vehicleDetails: VehicleDetails | null;
  dateRange: [string, string];
}

interface TemplateProps extends FuelSummaryReportProps {
  templateRef: React.RefObject<HTMLDivElement>;
}

const FuelReportTemplate = ({
  templateRef,
  filteredFuelData,
  fuelSummary,
  vehicleDetails,
  dateRange,
}: TemplateProps) => {
  const dateLabel = dateRange[0] && dateRange[1] ? `${dateRange[0]} to ${dateRange[1]}` : 'All Dates';

  const summaryItems: [string, string][] = [
    ['Vehicle Number', vehicleDetails?.plateNumber ?? 'N/A'],
    ['Fuel Type', vehicleDetails?.fuelType ?? 'N/A'],
    ['Fuel Efficiency', `${vehicleDetails?.fuelEfficiency ?? 0} KM/L`],
    ['Date Range', dateLabel],
    ['Total Fuel Filled', `${fuelSummary?.totalFuelFilled ?? 0} L`],
    ['Total Fuel Consumed', `${fuelSummary?.totalFuelConsumed ?? 0} L`],
    ['Total Fuel Cost', `${fuelSummary?.totalFuelCost ?? 0} LKR`],
    ['Average Fuel Usage', `${fuelSummary?.averageFuelUsage ?? 0} L/entry`],
    ['Total Distance Traveled', `${fuelSummary?.totalDistanceTraveled ?? 0} Km`],
    ['Avg Cost per Km', `${fuelSummary?.costPerKm ?? 0} LKR`],
  ];

  const tableHeaders = [
    'Date',
    'Fuel Filled (L)',
    'Fuel Used (L)',
    'Amount (LKR)',
    'Odometer (Km)',
    'Remaining Fuel (L)',
  ];

  return (
    <div
      ref={templateRef}
      className="report report--w900"
      style={{ '--report-accent': '#1a6eb5', fontSize: '12px' } as React.CSSProperties}
    >
      <div className="report__header">
        <span className="report__title">Fuel Summary Report</span>
        <span className="report__generated">Generated: {new Date().toLocaleString()}</span>
      </div>

      <div className="report__section-title">Summary</div>
      <div className="report__grid report__grid--mb24">
        {summaryItems.map(([label, value]) => (
          <div key={label} className="report__row">
            <div className="report__cell-label" style={{ '--section-bg': '#dbeafe' } as React.CSSProperties}>
              {label}
            </div>
            <div className="report__cell-value">{value}</div>
          </div>
        ))}
      </div>

      <div className="report__section-title">Fuel History</div>
      <table className="report__table report__table--fs11">
        <thead>
          <tr className="report__thead-row">
            {tableHeaders.map((h) => (
              <th key={h} className="report__th report__th--pad7">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredFuelData.map((row, index) => {
            const remainingCell = row.isFuelInvalid ? (
              <span className="report__invalid">Invalid</span>
            ) : (
              row.remainingFuel ?? '-'
            );
            return (
              <tr key={row.date ?? index} className={index % 2 === 0 ? 'report__tr--even' : 'report__tr--odd'}>
                <td className="report__td report__td--pad6">{row.date ?? '-'}</td>
                <td className="report__td report__td--pad6">{row.fuelFilled ?? '-'}</td>
                <td className="report__td report__td--pad6">{row.fuelUsed ?? '-'}</td>
                <td className="report__td report__td--pad6">{row.amount ?? '-'}</td>
                <td className="report__td report__td--pad6">{row.odometer ?? '-'}</td>
                <td className="report__td report__td--pad6">{remainingCell}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="report__footer">
        <span>RouteSonar — {vehicleDetails?.plateNumber} — Fuel Report</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

FuelReportTemplate.displayName = 'FuelReportTemplate';

const FuelSummaryReport = ({
  filteredFuelData,
  fuelSummary,
  vehicleDetails,
  dateRange,
}: FuelSummaryReportProps): JSX.Element => {
  const templateRef = useRef<HTMLDivElement>(null);
  const reportName =
    dateRange[0] && dateRange[1]
      ? `fuel-report-${dateRange[0]}-to-${dateRange[1]}.pdf`
      : `fuel-report-${vehicleDetails?.plateNumber ?? 'all'}.pdf`;
  const { generate, loading } = useGeneratePDF(templateRef, {
    fileName: reportName,
    orientation: 'landscape',
    avoidRowCuts: true,
  });

  return (
    <>
      <FuelReportTemplate
        templateRef={templateRef}
        filteredFuelData={filteredFuelData}
        fuelSummary={fuelSummary}
        vehicleDetails={vehicleDetails}
        dateRange={dateRange}
      />
      <Button
        icon={<FileDoneOutlined />}
        loading={loading}
        onClick={generate}
        disabled={!filteredFuelData || filteredFuelData.length === 0}
      >
        {loading ? 'Generating...' : 'Download Report'}
      </Button>
    </>
  );
};

export default FuelSummaryReport;
